import { scheduleOnIdle } from '@client/utils/scheduling/scheduleOnIdle';
import { LOCAL_STORAGE_KEY } from '@shared/constants';
import { useEffect, useState } from 'react';

export function useFingerprint() {
    const [visitorId, setVisitorId] = useState<string | null>(() => {
        try {
            return localStorage.getItem(LOCAL_STORAGE_KEY.FINGERPRINT) || null;
        } catch {
            return null;
        }
    });

    useEffect(() => {
        if (visitorId) return;

        let cancelled = false;

        // A browser's first visit computes the fingerprint (~130 ms of main thread at 4x CPU
        // throttling, the heaviest boot task in a profile of the public complaint page), and
        // first visits are the invite, attestation and lodgement links. Nothing on screen
        // needs it, and requests already go without the header until it is stored, so wait
        // until the page has loaded and settled.
        scheduleOnIdle(() => {
            if (cancelled) return;
            import('@fingerprintjs/fingerprintjs')
                .then(({ default: FingerprintJS }) => FingerprintJS.load())
                .then((fp) => fp.get())
                .then((result) => {
                    if (cancelled) return;
                    const newVisitorId = result.visitorId;
                    try {
                        localStorage.setItem(LOCAL_STORAGE_KEY.FINGERPRINT, newVisitorId);
                    } catch {
                        // Storage unavailable — fingerprint won't be persisted or sent in headers
                    }
                    setVisitorId(newVisitorId);
                })
                .catch(() => {
                    // Fingerprinting is best-effort — swallow chunk load / ad-blocker failures
                });
        });

        return () => {
            cancelled = true;
        };
    }, [visitorId]);

    return visitorId;
}
